import { useEffect, useRef, useState } from "react";
export interface SelectOption {
  label: string;
  value: string | number;
}

interface CommonSelectProps {
  label?: string;
  options: string[] | SelectOption[];
  value?: string; // stored as value only
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  required?: boolean;
  errorMessage?: string; // Add error message prop
}

const CommonSelect: React.FC<CommonSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  searchable = false,
  required = false,
  errorMessage, // Add error message prop
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ✅ Normalize options into [{ label, value }]
  const normalizedOptions: SelectOption[] = options.map(opt =>
    typeof opt === "string" ? { label: opt, value: opt } : opt
  );

  // ✅ Find selected label based on value
  const selectedLabel =
    normalizedOptions.find(opt => opt.value === value)?.label ?? "";

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Filter logic
  const filteredOptions = normalizedOptions.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ Handle selection
  const handleSelect = (option: SelectOption) => {
    onChange?.(option.value as string); // ✅ send only the value
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div className="relative flex flex-col" ref={dropdownRef}>
      {label && (
        <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Trigger */}
        <div
          className={`w-full rounded-md border border-[#DFE7EC] px-3 py-2.5 text-[15px] font-medium text-gray-700 transition ${
            disabled
              ? "cursor-not-allowed bg-[rgba(112,112,112,0.3)] text-[#2F363F]"
              : `cursor-pointer bg-white focus-within:border-[#66A0CD] ${errorMessage ? "border-red-500" : "hover:border-[#66A0CD]"}`
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {selectedLabel ? (
            <span className="font-sans text-[16px] font-semibold text-[#2F363F]">
              {selectedLabel}
            </span>
          ) : (
            <span className="font-sans text-[16px] font-semibold text-[#BECAD0]">
              {placeholder}
            </span>
          )}

          <svg
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#000]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-[10px] border border-[#f5f5f5] bg-white p-2 shadow-[0_2px_20px_0_rgba(35,105,142,0.12)]">
            {searchable && (
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search..."
                autoFocus
                className="w-full border-b border-gray-200 px-3 py-2 text-[14px] text-gray-700 outline-none focus:border-[#66A0CD]"
              />
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  className={`cursor-pointer rounded-[8px] px-2 py-2 font-sans text-[16px] font-semibold text-[#707070] hover:bg-[#E4FFFB] hover:text-[#127B8D] ${
                    value === opt.value
                      ? "bg-[#E4FFFB] font-semibold text-gray-800"
                      : ""
                  }`}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-[14px] text-gray-400">
                No results found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};

export default CommonSelect;
